/**
 * Onglet Calendrier des transferts d'un contrat (vue mensuelle).
 * Lecture seule pour le client.
 *
 * PM Spec 06/05/2026 — Phase 1 G3
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const STATUS_BG = {
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

const ClientContractCalendarTab = ({ contractId }) => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(() => new Date());

  const fetchRuns = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contracts/${contractId}/runs?limit=500`);
      setRuns(r.data?.items || r.data || []);
    } catch (err) {
      console.error('Error fetching runs:', err);
    } finally { setLoading(false); }
  }, [contractId]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Group runs by date
  const runsByDay = useMemo(() => {
    const map = {};
    runs.forEach(r => {
      const date = r.plannedDate || r.scheduledDate || r.date;
      if (!date) return;
      const key = String(date).slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }, [runs]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const year = month.getFullYear();
    const m = month.getMonth();
    const firstDay = new Date(year, m, 1);
    const lastDay = new Date(year, m + 1, 0);
    const offset = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, m, d);
      days.push(date);
    }
    return days;
  }, [month]);

  const monthLabel = month.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const prev = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const next = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const today = () => setMonth(new Date());

  const isToday = (d) => d && d.toDateString() === new Date().toDateString();

  return (
    <Card data-testid="client-contract-calendar-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CalendarDays className="w-5 h-5 text-gray-400" />
          Calendrier des transferts
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={prev}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium capitalize min-w-[150px] text-center">{monthLabel}</span>
          <Button variant="ghost" size="icon" onClick={next}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={today}>Aujourd'hui</Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 text-xs font-medium text-slate-500 mb-1">
              {DAYS.map(d => <div key={d} className="text-center py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((d, idx) => {
                if (!d) return <div key={idx} />;
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const dayRuns = runsByDay[key] || [];
                return (
                  <div key={idx}
                       className={`min-h-[80px] border rounded p-1 text-xs ${
                         isToday(d) ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-200'
                       }`}>
                    <div className={`text-right text-[10px] font-medium ${isToday(d) ? 'text-emerald-700' : 'text-slate-500'}`}>
                      {d.getDate()}
                    </div>
                    <div className="space-y-0.5 mt-1">
                      {dayRuns.slice(0, 3).map(r => (
                        <button
                          key={r.id}
                          onClick={() => navigate(`/mes-contrats/${contractId}/transferts/${r.id}`)}
                          className={`w-full text-left rounded px-1 py-0.5 text-[10px] truncate ${STATUS_BG[r.status] || 'bg-slate-100 text-slate-700'} hover:opacity-80 transition-opacity`}
                          title={`${r.runNumber || r.shortReference || r.id} - ${r.status}`}
                          data-testid={`calendar-run-${r.id}`}
                        >
                          {r.shift === 'night' ? <Moon className="w-2 h-2 inline mr-0.5" /> : <Sun className="w-2 h-2 inline mr-0.5" />}
                          {r.shortReference || r.runNumber?.split('-').pop() || 'RUN'}
                        </button>
                      ))}
                      {dayRuns.length > 3 && (
                        <div className="text-[9px] text-slate-500 italic">+ {dayRuns.length - 3}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {Object.entries(STATUS_BG).map(([k, cls]) => (
                <Badge key={k} className={`${cls} border-0`}>
                  {k === 'pending' ? 'Planifié' :
                   k === 'in_progress' ? 'En cours' :
                   k === 'completed' ? 'Terminé' : 'Annulé'}
                </Badge>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientContractCalendarTab;
